import {
  browserSessionPersistence,
  onAuthStateChanged,
  OAuthProvider,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { firebaseConfigured, requireFirebaseAuth, requireFirebaseFunctions } from '../lib/firebase';
import type { AppUser, SessionPayload } from '../types/auth';
import { AuthContext, type AuthContextValue } from './auth-context';

const demoUser: AppUser = {
  uid: 'local-demo',
  email: 'francesco.pace@justbit.it',
  firstName: 'Francesco',
  lastName: 'Pace',
  displayName: 'Francesco Pace',
  role: 'superadmin',
  demo: true,
};

async function loadAppSession(firebaseUser: User): Promise<AppUser> {
  const getCurrentSession = httpsCallable<void, SessionPayload>(
    requireFirebaseFunctions(),
    'getCurrentSession',
  );
  const result = await getCurrentSession();
  return {
    ...result.data.user,
    displayName: result.data.user.displayName || firebaseUser.displayName || 'Utente Justbit',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(firebaseConfigured);
  const interactiveSignIn = useRef(false);
  const demoAvailable = import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEMO_MODE !== 'false';

  useEffect(() => {
    if (!firebaseConfigured) return;

    const auth = requireFirebaseAuth();
    void setPersistence(auth, browserSessionPersistence);

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (interactiveSignIn.current) return;
      setLoading(true);
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        setUser(await loadAppSession(firebaseUser));
      } catch {
        await firebaseSignOut(auth).catch(() => undefined);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const signInWithJustbit = useCallback(async () => {
    const auth = requireFirebaseAuth();
    const provider = new OAuthProvider('oidc.justbit');
    interactiveSignIn.current = true;
    setLoading(true);
    try {
      const credential = await signInWithPopup(auth, provider);
      setUser(await loadAppSession(credential.user));
    } catch (error) {
      await firebaseSignOut(auth).catch(() => undefined);
      setUser(null);
      throw error;
    } finally {
      interactiveSignIn.current = false;
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const auth = requireFirebaseAuth();
    interactiveSignIn.current = true;
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const sessionUser = await loadAppSession(credential.user);
      setUser(sessionUser);
    } catch (error) {
      await firebaseSignOut(auth).catch(() => undefined);
      setUser(null);
      throw error;
    } finally {
      interactiveSignIn.current = false;
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(requireFirebaseAuth(), email);
  }, []);

  const signOut = useCallback(async () => {
    if (firebaseConfigured) await firebaseSignOut(requireFirebaseAuth());
    setUser(null);
  }, []);

  const enterDemo = useCallback(() => {
    if (demoAvailable) setUser(demoUser);
  }, [demoAvailable]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    configurationReady: firebaseConfigured,
    demoAvailable,
    signInWithJustbit,
    signIn,
    resetPassword,
    signOut,
    enterDemo,
  }), [demoAvailable, loading, resetPassword, signIn, signInWithJustbit, signOut, enterDemo, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
